import { Link } from "react-router-dom";

function LegalLayout({ title, lastUpdated, children, testId }) {
  return (
    <div className="overflow-x-hidden" data-testid={testId}>
      <section className="pt-32 pb-16" style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 text-sm mb-5" style={{ color: '#B9DCD2' }}>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">{title}</span>
          </div>
          <h1 className="text-4xl font-light text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
          {lastUpdated && <p className="text-sm" style={{ color: '#B9DCD2' }}>Last updated: {lastUpdated}</p>}
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-16">
        {children}
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-10">
    {title && <h2 className="text-xl font-semibold text-gray-900 mb-4 mt-8" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>}
    {children}
  </div>
);
const P = ({ children }) => <p className="text-gray-600 leading-relaxed mb-4 text-sm">{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm mb-4 ml-4">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="25 November, 2025" testId="terms-page">
      <P>
        Please read these Terms of Service ("Terms") carefully before using the Technofocus website (technofocus.ai) and its associated platforms, including LevelUp, EventsUp, and StepUp ("Services"). By accessing or using our Services, you agree to be bound by these Terms.
      </P>
      <P>These Terms are issued by Technofocus Pte. Ltd. ("Technofocus," "we," "us," or "our"), headquartered in Singapore.</P>

      <Section title="1. Acceptance of Terms">
        <P>By accessing or using any Technofocus Service, you represent that you are at least 18 years old, have read and understood these Terms, and agree to be legally bound by them. If you are using the Services on behalf of an organization, you represent that you have authority to bind that organization to these Terms.</P>
      </Section>

      <Section title="2. Use of Services">
        <P>You agree to use our Services only for lawful purposes and in accordance with these Terms. You must not:</P>
        <UL items={[
          "Use the Services in any way that violates applicable local, national, or international laws or regulations.",
          "Transmit unsolicited or unauthorized advertising or promotional material.",
          "Attempt to gain unauthorized access to any part of our Services or related systems.",
          "Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Services.",
          "Reproduce, duplicate, copy, sell, resell, or exploit any portion of the Services without our express written permission.",
          "Upload or transmit any content that is harmful, offensive, or infringes third-party intellectual property rights.",
        ]} />
      </Section>

      <Section title="3. Intellectual Property">
        <P>All content on our websites and platforms — including text, graphics, logos, course materials, software, and trademarks — is the property of Technofocus or its licensors and is protected by applicable intellectual property laws.</P>
        <P>You are granted a limited, non-exclusive, non-transferable licence to access and use the content for your personal, non-commercial purposes. You must not modify, distribute, reproduce, or create derivative works without our prior written consent.</P>
        <P>Technofocus acknowledges the intellectual property rights of Microsoft Corporation. Any Microsoft trademarks, logos, or course content used on our platforms are used with authorization as part of our Microsoft Learning Partner relationship.</P>
      </Section>

      <Section title="4. Course Enrolment and Learning Platforms">
        <P>Access to LevelUp, StepUp, and EventsUp platforms is subject to the specific terms and conditions of each platform. Enrolment in Microsoft technology content is subject to eligibility criteria set by Microsoft and your sponsoring partner organization.</P>
        <P>Technofocus reserves the right to modify, suspend, or discontinue any course, platform feature, or service at any time with reasonable notice.</P>
      </Section>

      <Section title="5. Disclaimer of Warranties">
        <P>The Services are provided on an "AS IS" and "AS AVAILABLE" basis without any warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.</P>
        <P>Technofocus does not warrant that the Services will be uninterrupted, error-free, or free of viruses or other harmful components.</P>
      </Section>

      <Section title="6. Limitation of Liability">
        <P>To the fullest extent permitted by applicable law, Technofocus, its directors, officers, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Services.</P>
        <P>In no event shall Technofocus's aggregate liability exceed the amount paid by you (if any) for accessing the Services in the twelve (12) months preceding the claim.</P>
      </Section>

      <Section title="7. Third-Party Links and Services">
        <P>Our Services may contain links to third-party websites or services, including Microsoft platforms. These links are provided for your convenience only. Technofocus does not endorse and is not responsible for the content, policies, or practices of any third-party websites.</P>
      </Section>

      <Section title="8. Privacy">
        <P>Your use of the Services is also governed by our <Link to="/privacy" className="text-ms-blue hover:underline">Privacy Statement</Link>, which is incorporated into these Terms by reference.</P>
      </Section>

      <Section title="9. Modifications to Terms">
        <P>Technofocus reserves the right to modify these Terms at any time. We will notify you of significant changes by posting the new Terms on our website with an updated "Last updated" date. Your continued use of the Services after any modifications constitutes your acceptance of the updated Terms.</P>
      </Section>

      <Section title="10. Governing Law and Jurisdiction">
        <P>These Terms shall be governed by and construed in accordance with the laws of Singapore. You agree to submit to the exclusive jurisdiction of the courts of Singapore for the resolution of any disputes arising out of or related to these Terms or your use of the Services.</P>
      </Section>

      <Section title="11. Contact Us">
        <P>If you have questions about these Terms, please contact us at:</P>
        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 mt-3">
          <p className="font-semibold text-gray-900 text-sm">Technofocus Pte. Ltd.</p>
          <p className="text-gray-500 text-sm">138 Robinson Road, #26-01 Oxley Tower, Singapore 068906</p>
          <a href="mailto:contact@technofocus.ai" className="text-ms-blue text-sm hover:underline">contact@technofocus.ai</a>
        </div>
      </Section>
    </LegalLayout>
  );
}
